import React, { useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactECharts from "echarts-for-react";
import { getFinanzasV2MensualData, getFinanzasV2AnualData } from "../../slices/dashboardFinanzasV2/thunk";
import {
  getFinanzasSaldosData,
  getFinanzasFacturasPendientesData,
  getFinanzasDsoData,
} from "../../slices/dashboardFinanzas/thunk";
import { I } from "../../Components/CMI/icons";
import { KPI, Card, CardHead, CardBody } from "../../Components/CMI/primitives";
import { Sparkline, fmtEur, fmtPct, fmtEurK } from "../../Components/CMI/charts";
import PageHead, { SectionHead, Footer } from "../../Layouts/CMI/PageHead";

const MESES_LABEL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MESES_FULL = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DONUT_COLORS = ["#2A5BFF","#0AB4D4","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899"];
const AREA_COLORS = {
  PERSONAL_PRODUCCION: "#3B82F6", EXTERNO_PRODUCCION: "#60A5FA",
  VENTAS: "#0AB4D4", MARKETING: "#F59E0B",
  "ADMINISTRACION Y FINANZAS": "#8B5CF6", SISTEMAS: "#10B981",
  "DIRECCION GENERAL": "#EC4899", CORPORATE_GENERAL: "#6B7280",
};

const r2 = (n) => parseFloat((n || 0).toFixed(2));

const tone = (val, sentido) => {
  if (val == null || val === 0) return "neu";
  if (sentido === "mayor_mejor") return val > 0 ? "pos" : "neg";
  if (sentido === "menor_mejor") return val < 0 ? "pos" : "neg";
  return Math.abs(val) <= 3 ? "neu" : val > 0 ? "neg" : "pos";
};

const fmtDelta = (v, unit = "€") => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  if (unit === "pp") return `${sign}${v.toFixed(1).replace(".",",")} pp`;
  if (unit === "%") return `${sign}${v.toFixed(1).replace(".",",")}%`;
  return `${sign}${fmtEur(v)}`;
};

const fmtDeltaArrow = (v, unit = "€") => {
  if (v == null) return "—";
  const arrow = v >= 0 ? "↑" : "↓";
  if (unit === "pp") return `${arrow} ${Math.abs(v).toFixed(1).replace(".",",")} pp`;
  if (unit === "%") return `${arrow} ${Math.abs(v).toFixed(1).replace(".",",")}%`;
  return `${arrow} ${fmtEur(Math.abs(v))}`;
};

/* ── ECharts option builder for evolución mensual ── */
const buildEvoOption = (months, real, ppto, color, currentIdx) => {
  const markLine = currentIdx != null && currentIdx >= 0 && currentIdx < months.length ? {
    silent: true, symbol: "none",
    data: [{ xAxis: currentIdx }],
    lineStyle: { color, width: 1.5, type: "solid", opacity: 0.35 },
    label: { show: false },
  } : undefined;

  return {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255,255,255,0.96)",
      borderColor: "var(--ce-border-subtle)",
      borderWidth: 1,
      textStyle: { fontSize: 11, color: "#333" },
      formatter: (params) => {
        const mes = params[0]?.axisValue || "";
        const lines = params.filter(p => p.value != null).map(p => {
          const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:4px"></span>`;
          return `${marker}${p.seriesName}: <b>${fmtEur(p.value)}</b>`;
        });
        if (!lines.length) return "";
        return `<div style="font-size:11px"><b>${mes}</b><br/>${lines.join("<br/>")}</div>`;
      },
    },
    grid: { top: 10, right: 12, bottom: 24, left: 12, containLabel: true },
    xAxis: {
      type: "category", data: months, boundaryGap: false,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        formatter: (v, i) => i === currentIdx ? `{cur|${v}}` : v,
        rich: { cur: { fontWeight: "bold", color: "#333", fontSize: 11 } },
        color: "#999",
      },
    },
    yAxis: {
      type: "value", splitNumber: 3,
      axisLabel: { fontSize: 9, color: "#bbb", formatter: (v) => fmtEurK(v) },
      splitLine: { lineStyle: { color: "#f0f0f0" } },
      axisLine: { show: false }, axisTick: { show: false },
    },
    series: [
      {
        name: "Real", type: "line", data: real, symbol: "circle",
        symbolSize: (_, params) => params.dataIndex === currentIdx ? 8 : 4,
        lineStyle: { width: 2.5, color },
        itemStyle: { color },
        areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: color + "20" }, { offset: 1, color: color + "02" }] } },
        markLine,
      },
      {
        name: "Presupuesto", type: "line", data: ppto, symbol: "none",
        lineStyle: { width: 1.5, color, type: "dashed", opacity: 0.5 },
        itemStyle: { color },
      },
    ],
  };
};

/* ── Donut ECharts ── */
const buildDonutOption = (data, centerLabel) => ({
  tooltip: {
    trigger: "item",
    formatter: ({ name, value, percent }) => `${name}<br/><b>${fmtEur(value)}</b> (${percent}%)`,
  },
  legend: { show: false },
  series: [{
    type: "pie",
    radius: ["52%", "78%"],
    center: ["50%", "50%"],
    avoidLabelOverlap: false,
    label: {
      show: true, position: "center",
      formatter: () => `{total|${centerLabel.value}}\n{sub|${centerLabel.label}}`,
      rich: {
        total: { fontSize: 16, fontWeight: 700, color: "#333", lineHeight: 24 },
        sub: { fontSize: 10, color: "#999", lineHeight: 16 },
      },
    },
    emphasis: {
      label: { show: true },
      itemStyle: { shadowBlur: 6, shadowOffsetX: 0, shadowColor: "rgba(0,0,0,0.15)" },
    },
    labelLine: { show: false },
    data: data.map(d => ({ name: d.label, value: d.value, itemStyle: { color: d.color } })),
  }],
});

/* ── Gauge circle ── */
const GaugeCircle = ({ pct, label, sentido, size = 90 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(150, pct || 0));
  const offset = circ - (clamped / 150) * circ;
  const color = sentido === "menor_mejor"
    ? (pct <= 100 ? "var(--ce-success)" : "var(--ce-danger)")
    : (pct >= 100 ? "var(--ce-success)" : "var(--ce-danger)");

  return (
    <div style={{ textAlign: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--ce-bg-2)" strokeWidth="7" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 600ms var(--ce-ease)" }} />
        <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize="16" fontWeight="600"
          fill="var(--ce-fg)">{pct != null ? `${Math.round(pct)}%` : "—"}</text>
      </svg>
      <div style={{ fontSize: 11, color: "var(--ce-fg-muted)", marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 10, color: "var(--ce-fg-faint)" }}>vs presupuesto</div>
    </div>
  );
};

/* ── Area row for table ── */
const AreaRow = ({ area, isGroup, indent }) => {
  const t = tone(area.pct, "contextual");
  return (
    <tr style={{ fontWeight: isGroup ? 600 : 400 }}>
      <td style={{ paddingLeft: indent ? 20 : 0, display: "flex", alignItems: "center", gap: 6 }}>
        {area.color && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: area.color, flexShrink: 0 }} />}
        {area.label}
      </td>
      <td className="right">{fmtEur(area.real)}</td>
      <td className="right">{fmtEur(area.presupuesto)}</td>
      <td className="right">{fmtDelta(area.eur)}</td>
      <td className="right">
        <span className={`kpi-pill ${t}`}>
          {area.pct != null ? `${area.pct >= 0 ? "+" : ""}${area.pct.toFixed(1).replace(".",",")}%` : "—"}
        </span>
      </td>
    </tr>
  );
};

/* ── Saldo bancos mini bar chart ── */
const SaldoBancosChart = ({ evolucion }) => {
  if (!evolucion || evolucion.length === 0) return null;
  const W = 180, H = 80, padB = 16, padT = 8;
  const max = Math.max(...evolucion.map(e => e.saldo)) * 1.15 || 1;
  const barW = (W / evolucion.length) * 0.6;
  const gap = (W / evolucion.length) * 0.4;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
      {evolucion.map((e, i) => {
        const x = i * (barW + gap) + gap / 2;
        const h = ((e.saldo || 0) / max) * (H - padB - padT);
        return (
          <g key={i}>
            <rect x={x} y={H - padB - h} width={barW} height={h} rx="2" fill="var(--ce-chart-1)" opacity="0.7" />
            <text x={x + barW / 2} y={H - 3} textAnchor="middle" fontSize="7" fill="var(--ce-fg-faint)">
              {MESES_LABEL[e.mes - 1]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ════════════════════════════════════════════
   DASHBOARD
   ════════════════════════════════════════════ */
const DashboardFinanzasV2 = () => {
  const dispatch = useDispatch();
  const { empresaId, year, escenario, mes, cierres: globalCierres } = useSelector((s) => s.Filters);
  const { mensual: data, anual: dataAnual, loading, loadingAnual } = useSelector((s) => s.DashboardFinanzasV2);
  const { saldos, facturasPendientes, dso } = useSelector((s) => s.DashboardFinanzas);

  const isProyeccion = escenario === "proyeccion_lineal";
  document.title = isProyeccion ? "Finanzas V2 — Anual | CMI" : "Finanzas V2 — Mensual | CMI";

  const load = useCallback((m) => {
    if (isProyeccion) {
      dispatch(getFinanzasV2AnualData());
    } else {
      dispatch(getFinanzasV2MensualData({ month: m || undefined }));
    }
    dispatch(getFinanzasSaldosData());
    dispatch(getFinanzasFacturasPendientesData());
    dispatch(getFinanzasDsoData());
  }, [dispatch, isProyeccion]);

  useEffect(() => { load(mes); }, [load, empresaId, year, mes]);

  useEffect(() => {
    const h = () => load(mes);
    window.addEventListener("cmi:refresh", h);
    return () => window.removeEventListener("cmi:refresh", h);
  }, [load, mes]);

  const mesesCerrados = globalCierres.filter(c => c.estado === "cerrado").map(c => c.mes);

  if (isProyeccion) {
    if (loadingAnual && !dataAnual) {
      return (
        <div className="content-body">
          <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Anual / Proyección de cierre" />
          <div style={{ textAlign: "center", padding: 80, color: "var(--ce-fg-muted)" }}>
            <I.Refresh size={24} className="spinning" />
            <p>Cargando proyección anual…</p>
          </div>
          <Footer />
        </div>
      );
    }

    if (!dataAnual || dataAnual.error === "no_cierre") {
      return (
        <div className="content-body">
          <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Anual / Proyección de cierre" />
          <div style={{ textAlign: "center", padding: 80, color: "var(--ce-fg-muted)" }}>
            <I.Calendar size={40} />
            <p style={{ marginTop: 12 }}>No hay ningún mes cerrado para {year}.</p>
            <p style={{ fontSize: 13 }}>Cierra un mes desde la administración para calcular la proyección.</p>
          </div>
          <Footer />
        </div>
      );
    }

    const { resumen: ra, evolucion: evoAnual, meta: metaAnual } = dataAnual;
    const nCerr = metaAnual?.n_cerrados || 0;
    const sparkVentas = evoAnual.map(e => e.ingresos);
    const sparkGastos = evoAnual.map(e => e.gastos);
    const sparkResultado = evoAnual.map(e => e.resultado);
    const sparkMargen = evoAnual.map(e => e.ingresos !== 0 ? e.resultado / e.ingresos * 100 : 0);

    const fmtDesv = (eur, pct) => {
      const sign = eur >= 0 ? "+" : "";
      const pctStr = pct != null ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1).replace(".",",")}%)` : "";
      return `${sign}${fmtEur(eur)}${pctStr}`;
    };

    const acumMargen = ra.ventas.acumulado !== 0
      ? r2((ra.ventas.acumulado - ra.gastos.acumulado) / ra.ventas.acumulado * 100) : null;

    return (
      <div className="content-body">
        <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Anual / Proyección de cierre" />

        <SectionHead icon={I.BarChart3} title={`Resumen ejecutivo – Proyección de cierre ${year} vs Presupuesto anual`} />
        <div className="grid gc-4">
          <KPI tier="hero" tone="primary" icon={I.TrendingUp} label="Previsión de ventas al cierre"
            value={fmtEur(ra.ventas.proyeccion)}
            delta={fmtDesv(ra.ventas.eur, ra.ventas.pct)}
            deltaTone={ra.ventas.eur >= 0 ? "pos" : "neg"}
            deltaRef={`Presupuesto: ${fmtEur(ra.ventas.presupuesto_anual)}`}
            extras={<>
              <span className="kpi-ref">Real YTD ({nCerr} meses): {fmtEur(ra.ventas.acumulado)}</span>
              <Sparkline values={sparkVentas} color="#2A5BFF" w={120} h={28} responsive />
            </>}
          />
          <KPI tier="hero" tone="warning" icon={I.Coin} label="Previsión de gastos al cierre"
            value={fmtEur(ra.gastos.proyeccion)}
            delta={fmtDesv(ra.gastos.eur, ra.gastos.pct)}
            deltaTone={ra.gastos.eur <= 0 ? "pos" : "neg"}
            deltaRef={`Presupuesto: ${fmtEur(ra.gastos.presupuesto_anual)}`}
            extras={<>
              <span className="kpi-ref">Real YTD ({nCerr} meses): {fmtEur(ra.gastos.acumulado)}</span>
              <Sparkline values={sparkGastos} color="#F59E0B" w={120} h={28} responsive />
            </>}
          />
          <KPI tier="hero" tone="success" icon={I.BarChart3} label="Previsión de resultado al cierre"
            value={fmtEur(ra.resultado.proyeccion)}
            delta={fmtDesv(ra.resultado.eur, ra.resultado.pct)}
            deltaTone={ra.resultado.eur >= 0 ? "pos" : "neg"}
            deltaRef={`Presupuesto: ${fmtEur(ra.resultado.presupuesto_anual)}`}
            extras={<>
              <span className="kpi-ref">Real YTD ({nCerr} meses): {fmtEur(ra.resultado.acumulado)}</span>
              <Sparkline values={sparkResultado} color="#10B981" w={120} h={28} responsive />
            </>}
          />
          <KPI tier="hero" tone="cyan" icon={I.Percent} label="Margen operativo al cierre"
            value={fmtPct(ra.margen.proyeccion)}
            delta={`${ra.margen.desviacion_pp >= 0 ? "+" : ""}${ra.margen.desviacion_pp.toFixed(1).replace(".",",")} pp`}
            deltaTone={ra.margen.desviacion_pp >= 0 ? "pos" : "neg"}
            deltaRef={`Presupuesto: ${fmtPct(ra.margen.presupuesto_anual)}`}
            extras={<>
              <span className="kpi-ref">Real YTD ({nCerr} meses): {acumMargen != null ? fmtPct(acumMargen) : "—"}</span>
              <Sparkline values={sparkMargen} color="#F97316" w={120} h={28} responsive />
            </>}
          />
        </div>

        {/* ══ BLOQUE B — KPIs estructurales proyectados vs Benchmark ══ */}
        {(() => {
          const kb = dataAnual.kpis_estructurales;
          if (!kb) return null;

          const fmtBench = (b) => b?.valor != null ? (b.valor >= 1000 ? fmtEur(b.valor) : fmtPct(b.valor)) : "—";
          const fmtPP = (pp) => pp != null ? `${pp >= 0 ? "+" : ""}${pp.toFixed(1).replace(".",",")} pp` : "—";
          const fmtDesvE = (eur, pct) => {
            if (eur == null) return "—";
            const pctStr = pct != null ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1).replace(".",",")}%)` : "";
            return `${eur >= 0 ? "+" : ""}${fmtEur(eur)}${pctStr}`;
          };
          const ppTone = (pp, sentido) => {
            if (pp == null || pp === 0) return "neu";
            if (sentido === "contextual") return "neu";
            if (sentido === "menor_mejor") return pp < 0 ? "pos" : "neg";
            return pp > 0 ? "pos" : "neg";
          };
          const eurTone = (eur, sentido) => {
            if (eur == null || eur === 0) return "neu";
            if (sentido === "menor_mejor") return eur < 0 ? "pos" : "neg";
            return eur > 0 ? "pos" : "neg";
          };

          const KPI_DEFS = [
            { key: "produccion_ingresos", label: "Producción / Ingresos", icon: I.Briefcase, color: "#2A5BFF", tone: "primary" },
            { key: "growth_ingresos", label: <>Growth (Ventas +<br/>Marketing) / Ingresos</>, icon: I.TrendingUp, color: "#10B981", tone: "success" },
            { key: "corporate_ingresos", label: "Corporate Support / Ingresos", icon: I.Settings, color: "#8B5CF6", tone: "warning" },
            { key: "ingreso_por_fte", label: <>Ingreso por empleado<br/><span style={{fontSize:10,fontWeight:400}}>(Ingresos / FTE)</span></>, icon: I.Users, color: "#0AB4D4", tone: "cyan" },
            { key: "ingreso_por_fte_produccion", label: <>Ingreso por empleado<br/>de Producción<br/><span style={{fontSize:10,fontWeight:400}}>(Ingresos / FTE productivo)</span></>, icon: I.Users, color: "#2A5BFF", tone: "primary" },
            { key: "coste_produccion_por_fte", label: <>Coste de Producción por<br/>empleado de Producción<br/><span style={{fontSize:10,fontWeight:400}}>(Coste Producción / FTE productivo)</span></>, icon: I.Briefcase, color: "#F59E0B", tone: "warning" },
          ];

          return (
            <>
              <SectionHead icon={I.Activity} title={`KPIs estructurales y productividad – Proyección de cierre ${year} vs Benchmark sectorial`} small />
              <div className="grid gc-3">
                {KPI_DEFS.slice(0, 3).map(({ key, label, icon, color, tone: t }) => {
                  const d = kb[key];
                  const benchVal = d.benchmark?.valor;
                  const isPct = d.unidad === "%";
                  return (
                    <KPI key={key} tone={t} icon={icon} label={label}
                      value={d.proyeccion != null ? (isPct ? fmtPct(d.proyeccion) : fmtEur(d.proyeccion)) : "—"}
                      delta={isPct ? fmtPP(d.desviacion_pp) : null}
                      deltaTone={isPct ? ppTone(d.desviacion_pp, d.sentido) : "neu"}
                      deltaRef={`Benchmark: ${benchVal != null ? fmtPct(benchVal) : "—"}`}
                      extras={<Sparkline values={d.sparkline} color={color} w={120} h={28} responsive />}
                      info={benchVal != null ? {
                        titulo: "Benchmark sectorial",
                        descripcion: `P25: ${d.benchmark.p25 != null ? fmtPct(d.benchmark.p25) : "—"} · Mediana: ${d.benchmark.mediana != null ? fmtPct(d.benchmark.mediana) : "—"} · P75: ${d.benchmark.p75 != null ? fmtPct(d.benchmark.p75) : "—"}`,
                        origen: `n = ${d.benchmark.n_observaciones ?? "—"} · Calidad: ${d.benchmark.nivel_evidencia ?? "—"} · ${d.benchmark.robustez ?? ""}`,
                      } : null}
                    />
                  );
                })}
              </div>
              <div className="grid gc-3" style={{ marginTop: 16 }}>
                {KPI_DEFS.slice(3).map(({ key, label, icon, color, tone: t }) => {
                  const d = kb[key];
                  const benchVal = d.benchmark?.valor;
                  const fteMedio = d.fte_medio;
                  const fteLabel = key === "ingreso_por_fte"
                    ? "FTE total medio" : "FTE producción medio";
                  return (
                    <KPI key={key} tone={t} icon={icon} label={label}
                      value={d.proyeccion != null ? fmtEur(d.proyeccion) : "—"}
                      delta={d.desviacion_eur != null ? fmtDesvE(d.desviacion_eur, d.desviacion_pct) : "—"}
                      deltaTone={d.desviacion_eur != null ? eurTone(d.desviacion_eur, d.sentido) : "neu"}
                      deltaRef={`Benchmark: ${benchVal != null ? fmtEur(benchVal) : "—"}`}
                      extras={<>
                        {fteMedio != null && fteMedio > 0 && (
                          <span className="kpi-ref">{fteLabel}: {fteMedio.toFixed(1)}</span>
                        )}
                        <Sparkline values={d.sparkline} color={color} w={120} h={28} responsive />
                      </>}
                      info={benchVal != null ? {
                        titulo: "Benchmark sectorial",
                        descripcion: `P25: ${d.benchmark.p25 != null ? fmtEur(d.benchmark.p25) : "—"} · Mediana: ${d.benchmark.mediana != null ? fmtEur(d.benchmark.mediana) : "—"} · P75: ${d.benchmark.p75 != null ? fmtEur(d.benchmark.p75) : "—"}`,
                        origen: `n = ${d.benchmark.n_observaciones ?? "—"} · Calidad: ${d.benchmark.nivel_evidencia ?? "—"} · ${d.benchmark.robustez ?? ""}`,
                      } : null}
                    />
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* ══ BLOQUE C+D — Evolución anual + Resumen por áreas (2 columnas) ══ */}
        {(() => {
          const evo = dataAnual.evolucion_anual;
          const at = dataAnual.areas_tabla;
          if (!evo || !at) return null;
          const nc = evo.n_cerrados;

          /* ── SVG Evolución anual acumulada ── */
          const W = 520, H = 280, padL = 60, padR = 16, padT = 24, padB = 28;
          const innerW = W - padL - padR;
          const innerH = H - padT - padB;
          const allVals = [...evo.real.ventas, ...evo.proy.ventas, ...evo.ppto.ventas,
                           ...evo.real.gastos, ...evo.proy.gastos, ...evo.ppto.gastos].filter(v => v != null);
          const maxVal = (Math.max(...allVals) || 1) * 1.08;
          const minVal = Math.min(0, ...evo.real.resultado, ...evo.proy.resultado, ...evo.ppto.resultado) * 1.08;
          const range = maxVal - minVal;
          const sx = (i) => padL + (innerW / 11) * i;
          const sy = (v) => padT + innerH - ((v - minVal) / range) * innerH;

          const makePath = (vals, startIdx = 0) =>
            vals.map((v, i) => `${i === 0 ? "M" : "L"} ${sx(startIdx + i)} ${sy(v)}`).join(" ");

          const SERIES = [
            { key: "ventas", color: "#2A5BFF", label: "Ventas" },
            { key: "gastos", color: "#EF4444", label: "Gastos" },
            { key: "resultado", color: "#10B981", label: "Resultado operativo" },
          ];

          const cutX = sx(nc - 1);

          const fmtPPvar = (pp) => {
            if (pp == null) return "—";
            const sign = pp > 0 ? "+" : "";
            return `${sign}${pp.toFixed(1).replace(".",",")} pp`;
          };
          const ppVarTone = (pp) => {
            if (pp == null || Math.abs(pp) < 0.05) return "";
            return pp > 0 ? "neg" : "pos";
          };

          const AREA_ROWS = [
            { key: "produccion", label: "Producción", bold: true },
            { key: "personal_produccion", label: "Personal Producción", indent: true },
            { key: "externo_produccion", label: "Externo Producción", indent: true },
            { key: "growth", label: "Growth (Ventas + Marketing)", bold: true },
            { key: "ventas", label: "Ventas", indent: true },
            { key: "marketing", label: "Marketing", indent: true },
            { key: "corporate_support", label: "Corporate Support", bold: true },
            { key: "admin_finanzas", label: "Administración y Finanzas", indent: true },
            { key: "sistemas", label: "Sistemas", indent: true },
            { key: "direccion_general", label: "Dirección General", indent: true },
            { key: "corporate_general", label: "Corporate general", indent: true },
          ];

          return (
            <div className="grid gc-2" style={{ marginTop: 24, alignItems: "start" }}>
              {/* ── Evolución anual ── */}
              <Card>
                <CardHead icon={I.BarChart3} title="Evolución anual" />
                <CardBody>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", marginBottom: 12, fontSize: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {SERIES.map(s => (
                        <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span style={{ display: "inline-block", width: 20, height: 3, background: s.color, borderRadius: 1 }} />
                          {s.label} (Real acumulado)
                        </span>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {SERIES.map(s => (
                        <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: 0.6 }}>
                          <span style={{ display: "inline-block", width: 20, height: 0, borderTop: `2px dashed ${s.color}` }} />
                          {s.label} (Presupuesto acumulado)
                        </span>
                      ))}
                    </div>
                  </div>
                  <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
                    {/* X labels */}
                    {MESES_LABEL.map((m, i) => (
                      <text key={m} x={sx(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="var(--ce-fg-faint)">{m}</text>
                    ))}
                    {/* Y grid */}
                    {[0, 0.25, 0.5, 0.75, 1].map(f => {
                      const v = minVal + range * f;
                      const yp = sy(v);
                      return (
                        <g key={f}>
                          <line x1={padL} x2={W - padR} y1={yp} y2={yp} stroke="var(--ce-border-subtle)" strokeWidth="0.5" />
                          <text x={padL - 6} y={yp + 3} textAnchor="end" fontSize="8" fill="var(--ce-fg-faint)">{fmtEurK(v)}</text>
                        </g>
                      );
                    })}
                    {/* Vertical cut line */}
                    <line x1={cutX} x2={cutX} y1={padT} y2={H - padB} stroke="var(--ce-fg-faint)" strokeWidth="1" strokeDasharray="4 3" />
                    <text x={cutX} y={padT - 6} textAnchor="middle" fontSize="8" fill="var(--ce-fg-muted)">Último mes cerrado</text>
                    {/* Presupuesto lines (dashed, all 12 months) */}
                    {SERIES.map(s => (
                      <path key={`ppto-${s.key}`} d={makePath(evo.ppto[s.key])} fill="none"
                        stroke={s.color} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.5" />
                    ))}
                    {/* Real lines (solid, months 1-nCerrados) */}
                    {SERIES.map(s => (
                      <path key={`real-${s.key}`} d={makePath(evo.real[s.key])} fill="none"
                        stroke={s.color} strokeWidth="2.5" />
                    ))}
                    {/* Projection lines (dashed-dot, months nCerrados-12) */}
                    {SERIES.map(s => (
                      <path key={`proy-${s.key}`} d={makePath(evo.proy[s.key], nc - 1)} fill="none"
                        stroke={s.color} strokeWidth="2" strokeDasharray="8 4" />
                    ))}
                    {/* Real dots */}
                    {SERIES.map(s =>
                      evo.real[s.key].map((v, i) => (
                        <circle key={`dot-${s.key}-${i}`} cx={sx(i)} cy={sy(v)} r="2.5" fill={s.color} />
                      ))
                    )}
                    {/* Projection dots */}
                    {SERIES.map(s =>
                      evo.proy[s.key].slice(1).map((v, i) => (
                        <circle key={`pdot-${s.key}-${i}`} cx={sx(nc + i)} cy={sy(v)} r="2" fill={s.color} opacity="0.5" />
                      ))
                    )}
                  </svg>
                </CardBody>
              </Card>

              {/* ── Resumen por áreas ── */}
              <Card>
                <CardHead icon={I.Layers} title="Resumen por áreas – Proyección de cierre" sub="% sobre ingresos" />
                <CardBody style={{ padding: "8px 12px" }}>
                  <table className="tbl" style={{ width: "100%", fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left" }}>Área</th>
                        <th className="right" style={{ fontSize: 10 }}>Proy. cierre</th>
                        <th className="right" style={{ fontSize: 10 }}>Presupuesto</th>
                        <th className="right" style={{ fontSize: 10 }}>Var. vs Presup.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {AREA_ROWS.map(({ key, label, bold, indent }) => {
                        const row = at[key];
                        if (!row) return null;
                        return (
                          <tr key={key} style={{ fontWeight: bold ? 600 : 400 }}>
                            <td style={{ paddingLeft: indent ? 18 : 0 }}>{label}</td>
                            <td className="right">{row.proy_pct != null ? fmtPct(row.proy_pct) : "—"}</td>
                            <td className="right">{row.ppto_pct != null ? fmtPct(row.ppto_pct) : "—"}</td>
                            <td className="right">
                              <span className={`kpi-pill ${ppVarTone(row.var_pp)}`}>
                                {fmtPPvar(row.var_pp)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: "2px solid var(--ce-border-strong)" }}>
                        <td>Total</td>
                        <td className="right">{at.total?.proy_pct != null ? fmtPct(at.total.proy_pct) : "—"}</td>
                        <td className="right">{at.total?.ppto_pct != null ? fmtPct(at.total.ppto_pct) : "—"}</td>
                        <td className="right">
                          <span className={`kpi-pill ${ppVarTone(at.total?.var_pp)}`}>
                            {fmtPPvar(at.total?.var_pp)}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </div>
          );
        })()}

        {/* ══ BLOQUE E+F+G — Gastos externos + Tesorería + Cumplimiento ══ */}
        {(() => {
          const ge = dataAnual.gastos_externos;
          const cu = dataAnual.cumplimiento;
          if (!ge || !cu) return null;

          const EXT_COLORS = ["#2A5BFF", "#0AB4D4", "#10B981", "#F59E0B", "#8B5CF6", "#6B7280"];

          const donutData = ge.detalle.map((g, i) => ({
            label: g.funcion,
            value: g.proyeccion,
            pct: g.porcentaje,
            ytd: g.ytd,
            color: EXT_COLORS[i % EXT_COLORS.length],
          }));

          // Cumplimiento gauges
          const cuGauges = [
            { label: "Ventas", pct: cu.ventas, sentido: "mayor_mejor" },
            { label: "Gastos", pct: cu.gastos, sentido: "menor_mejor" },
            { label: "Resultado", pct: cu.resultado, sentido: "mayor_mejor" },
          ];

          return (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 24, alignItems: "start" }}>
              {/* ── Gastos externos ── */}
              <Card>
                <CardHead icon={I.PieChart} title="Distribución de gastos externos por función" sub="(Proyección de cierre)" />
                <CardBody>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <div style={{ flex: "0 0 180px" }}>
                      <ReactECharts
                        option={buildDonutOption(donutData, { value: fmtEur(ge.total_proyeccion || 0), label: "Total ext. proy." })}
                        style={{ height: 180 }} opts={{ renderer: "svg" }} notMerge
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      {donutData.map(d => (
                        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12 }}>
                          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{d.label}</span>
                          <span style={{ fontWeight: 600 }}>{d.pct.toFixed(1).replace(".",",")}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* ── Columna derecha: Tesorería + Cumplimiento ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Tesorería */}
                <Card>
                  <CardHead icon={I.Bank} title="Capital circulante y tesorería" />
                  <CardBody style={{ padding: "10px 14px" }}>
                    {(() => {
                      const saldoTotal = saldos?.total_global;
                      const pendiente = facturasPendientes?.total_pendiente;
                      const dsoDias = dso?.dso_dias;
                      const gastoMesProy = ra?.gastos?.proyeccion ? ra.gastos.proyeccion / 12 : null;
                      const runway = saldoTotal != null && gastoMesProy ? (saldoTotal / gastoMesProy).toFixed(1) : null;

                      const items = [
                        { label: "DSO (días)", value: dsoDias != null ? Math.round(dsoDias) : "—" },
                        { label: "Pendiente de cobro", value: pendiente != null ? fmtEur(pendiente) : "—" },
                        { label: "Runway (meses)", value: runway ?? "—" },
                        { label: "Saldo bancos", value: saldoTotal != null ? fmtEur(saldoTotal) : "—" },
                      ];

                      return (
                        <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
                          <tbody>
                            {items.map(item => (
                              <tr key={item.label}>
                                <td style={{ color: "var(--ce-fg-muted)", paddingRight: 12 }}>{item.label}</td>
                                <td style={{ fontWeight: 600, textAlign: "right" }}>{item.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </CardBody>
                </Card>

                {/* Cumplimiento presupuesto */}
                <Card>
                  <CardHead icon={I.Target} title="Cumplimiento presupuesto" sub="Proyección de cierre vs Presupuesto anual" />
                  <CardBody>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                      {cuGauges.map(g => (
                        <GaugeCircle key={g.label} pct={g.pct} label={g.label} sentido={g.sentido} />
                      ))}
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: cu.margen.desviacion_pp >= 0 ? "var(--ce-success)" : "var(--ce-danger)" }}>
                          {fmtPct(cu.margen.proyeccion)}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--ce-fg-muted)", marginTop: 2 }}>Margen operativo</div>
                        <div style={{ fontSize: 10, color: "var(--ce-fg-faint)" }}>
                          Presup: {fmtPct(cu.margen.presupuesto)}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: cu.margen.desviacion_pp >= 0 ? "var(--ce-success)" : "var(--ce-danger)" }}>
                          {cu.margen.desviacion_pp >= 0 ? "+" : ""}{cu.margen.desviacion_pp.toFixed(1).replace(".",",")} pp
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          );
        })()}

        <Footer />
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="content-body">
        <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Mensual / Cierre de mes" />
        <div style={{ textAlign: "center", padding: 80, color: "var(--ce-fg-muted)" }}>
          <I.Refresh size={24} className="spinning" />
          <p>Cargando datos del mes…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data || data.error === "no_cierre") {
    return (
      <div className="content-body">
        <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Mensual / Cierre de mes" />
        <div style={{ textAlign: "center", padding: 80, color: "var(--ce-fg-muted)" }}>
          <I.Calendar size={40} />
          <p style={{ marginTop: 12 }}>No hay ningún mes cerrado para {year}.</p>
          <p style={{ fontSize: 13 }}>Cierra un mes desde la administración para ver el dashboard.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const { resumen, kpis_estructurales: kpis, evolucion, areas, areas_agrupadas, gastos_externos, cumplimiento, fte, meta, sparklines_b: spB, saldos_evo } = data;
  const mesLabel = MESES_FULL[(meta?.mes || 1) - 1];

  // ── Evolución: preparar datos por métrica (12 meses, real hasta mes calendario) ──
  const evoMonths = evolucion.map(e => MESES_LABEL[e.mes - 1]);
  const evoIngresosReal = evolucion.map(e => e.real ? (e.real.ingresos || 0) : null);
  const evoIngresosPpto = evolucion.map(e => e.ppto ? (e.ppto.ingresos || 0) : null);
  const evoGastosReal = evolucion.map(e => e.real ? (e.real.gastos || 0) : null);
  const evoGastosPpto = evolucion.map(e => e.ppto ? (e.ppto.gastos || 0) : null);
  const evoResultadoReal = evolucion.map(e => e.real ? (e.real.resultado || 0) : null);
  const evoResultadoPpto = evolucion.map(e => e.ppto ? (e.ppto.resultado || 0) : null);
  const evoCurrentIdx = evoMonths.indexOf(MESES_LABEL[(meta?.mes || 1) - 1]);

  // ── Donut data ──
  const donutData = (gastos_externos?.detalle || []).map((g, i) => ({
    label: g.funcion, value: g.importe, color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  // ── Areas: build rows with colors ──
  const makeGroupRow = (key, label, color) => {
    const g = areas_agrupadas[key] || { real: 0, presupuesto: 0, eur: 0, pct: null };
    return { label, color, ...g };
  };

  const makeSubRow = (a) => ({ ...a, color: AREA_COLORS[a.area] });

  const areasProd = areas.filter(a => a.grupo_kpi === "PRODUCCION");
  const areasGrowth = areas.filter(a => a.grupo_kpi === "GROWTH");
  const areasCorp = areas.filter(a => a.grupo_kpi === "CORPORATE_SUPPORT");

  // Total variación %
  const totalPct = resumen.gastos.presupuesto ? parseFloat(((resumen.gastos.real / resumen.gastos.presupuesto - 1) * 100).toFixed(1)) : null;

  // ── Saldo bancos evolución (desde Holded dailyledger cuentas 572) ──
  const saldoEvo = saldos_evo || [];

  return (
    <div className="content-body">
      <PageHead icon={I.BarChart3} title="Dashboard Finanzas" sub="Vista Mensual / Cierre de mes" />

      {/* ══ BLOQUE A — Resumen ejecutivo mensual ══ */}
      <SectionHead icon={I.BarChart3} title={`Resumen ejecutivo mensual – ${mesLabel} ${year} vs Presupuesto mensual`} />
      <div className="grid gc-4">
        <KPI tier="hero" tone="primary" icon={I.TrendingUp} label="Ventas del mes"
          value={fmtEur(resumen.ventas.real)}
          delta={fmtDeltaArrow(resumen.ventas.eur)} deltaTone={tone(resumen.ventas.eur, "mayor_mejor")}
          deltaRef={`Presupuesto: ${fmtEur(resumen.ventas.presupuesto)}`}
          extras={<span className="kpi-ref">{fmtDelta(resumen.ventas.pct, "%")} vs presupuesto mensual</span>}
        />
        <KPI tier="hero" tone="warning" icon={I.Coin} label="Gastos del mes"
          value={fmtEur(resumen.gastos.real)}
          delta={fmtDeltaArrow(resumen.gastos.eur)} deltaTone={tone(resumen.gastos.eur, "menor_mejor")}
          deltaRef={`Presupuesto: ${fmtEur(resumen.gastos.presupuesto)}`}
          extras={<span className="kpi-ref">{fmtDelta(resumen.gastos.pct, "%")} vs presupuesto mensual</span>}
        />
        <KPI tier="hero" tone="success" icon={I.BarChart3} label="Resultado del mes"
          value={fmtEur(resumen.resultado.real)}
          delta={fmtDeltaArrow(resumen.resultado.eur)} deltaTone={tone(resumen.resultado.eur, "mayor_mejor")}
          deltaRef={`Presupuesto: ${fmtEur(resumen.resultado.presupuesto)}`}
          extras={<span className="kpi-ref">{fmtDelta(resumen.resultado.pct, "%")} vs presupuesto mensual</span>}
        />
        <KPI tier="hero" tone="cyan" icon={I.Percent} label="Margen operativo del mes"
          value={fmtPct(resumen.margen.real)}
          delta={fmtDeltaArrow(resumen.margen.desviacion_pp, "pp")}
          deltaTone={tone(resumen.margen.desviacion_pp, "mayor_mejor")}
          deltaRef={`Presupuesto: ${fmtPct(resumen.margen.presupuesto)}`}
        />
      </div>

      {/* ══ BLOQUE B — KPIs estructurales ══ */}
      <SectionHead icon={I.Activity} title={`KPIs estructurales y productividad – Mes cerrado vs Presupuesto mensual`} small />
      <div className="grid gc-3">
        <KPI tone="primary" icon={I.Briefcase} label="Producción / Ingresos"
          value={fmtPct(kpis.produccion_ingresos.real)}
          deltaRef={`Presupuesto: ${fmtPct(kpis.produccion_ingresos.presupuesto)}`}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.produccion_ingresos.desviacion_pp, "menor_mejor")}`}>{fmtDeltaArrow(kpis.produccion_ingresos.desviacion_pp, "pp")}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.produccion?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.produccion} color="#2A5BFF" w={120} h={36} responsive /></div>}
          </div>}
        />
        <KPI tone="success" icon={I.TrendingUp}
          label={<>Growth (Ventas +<br/>Marketing) / Ingresos</>}
          value={fmtPct(kpis.growth_ingresos.real)}
          deltaRef={`Presupuesto: ${fmtPct(kpis.growth_ingresos.presupuesto)}`}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.growth_ingresos.desviacion_pp, "menor_mejor")}`}>{fmtDeltaArrow(kpis.growth_ingresos.desviacion_pp, "pp")}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.growth?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.growth} color="#10B981" w={120} h={36} responsive /></div>}
          </div>}
        />
        <KPI tone="warning" icon={I.Settings}
          label="Corporate Support / Ingresos"
          value={fmtPct(kpis.corporate_ingresos.real)}
          deltaRef={`Presupuesto: ${fmtPct(kpis.corporate_ingresos.presupuesto)}`}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.corporate_ingresos.desviacion_pp, "menor_mejor")}`}>{fmtDeltaArrow(kpis.corporate_ingresos.desviacion_pp, "pp")}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.corporate?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.corporate} color="#F59E0B" w={120} h={36} responsive /></div>}
          </div>}
        />
      </div>
      <div className="grid gc-3" style={{ marginTop: 16 }}>
        <KPI tone="cyan" icon={I.Users}
          label={<>Ingreso por empleado<br/><span style={{fontSize:10,fontWeight:400}}>(Ingresos / FTE)</span></>}
          value={kpis.ingreso_por_fte.real != null ? fmtEur(kpis.ingreso_por_fte.real) : "—"}
          deltaRef={kpis.ingreso_por_fte.presupuesto != null ? `Presupuesto: ${fmtEur(kpis.ingreso_por_fte.presupuesto)}` : null}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.ingreso_por_fte.desviacion_eur, "mayor_mejor")}`}>{fmtDeltaArrow(kpis.ingreso_por_fte.desviacion_eur)}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.ingreso_fte?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.ingreso_fte} color="#06B6D4" w={120} h={36} responsive /></div>}
          </div>}
        />
        <KPI tone="primary" icon={I.Users}
          label={<>Ingreso por empleado<br/>de Producción<br/><span style={{fontSize:10,fontWeight:400}}>(Ingresos / FTE productivo)</span></>}
          value={kpis.ingreso_por_fte_produccion.real != null ? fmtEur(kpis.ingreso_por_fte_produccion.real) : "—"}
          deltaRef={kpis.ingreso_por_fte_produccion.presupuesto != null ? `Presupuesto: ${fmtEur(kpis.ingreso_por_fte_produccion.presupuesto)}` : null}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.ingreso_por_fte_produccion.desviacion_eur, "mayor_mejor")}`}>{fmtDeltaArrow(kpis.ingreso_por_fte_produccion.desviacion_eur)}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.ingreso_fte_prod?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.ingreso_fte_prod} color="#2A5BFF" w={120} h={36} responsive /></div>}
          </div>}
        />
        <KPI tone="warning" icon={I.Briefcase}
          label={<>Coste de Producción por<br/>empleado de Producción<br/><span style={{fontSize:10,fontWeight:400}}>(Coste Producción / FTE productivo)</span></>}
          value={kpis.coste_produccion_por_fte.real != null ? fmtEur(kpis.coste_produccion_por_fte.real) : "—"}
          deltaRef={kpis.coste_produccion_por_fte.presupuesto != null ? `Presupuesto: ${fmtEur(kpis.coste_produccion_por_fte.presupuesto)}` : null}
          extras={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}>
            <div><span className={`kpi-pill ${tone(kpis.coste_produccion_por_fte.desviacion_eur, "menor_mejor")}`}>{fmtDeltaArrow(kpis.coste_produccion_por_fte.desviacion_eur)}</span>{" "}<span className="kpi-ref">vs presupuesto mensual</span></div>
            {spB?.coste_prod_fte?.length > 1 && <div style={{ width: "50%", flexShrink: 0 }}><Sparkline values={spB.coste_prod_fte} color="#F59E0B" w={120} h={36} responsive /></div>}
          </div>}
        />
      </div>

      {/* ══ FILA 1 — Evolución mensual (3 cards ECharts) ══ */}
      <div className="grid gc-3" style={{ marginTop: 24, alignItems: "start" }}>
        <Card>
          <CardHead icon={I.TrendingUp} title="Evolución mensual – Ventas" />
          <CardBody style={{ padding: "8px 12px" }}>
            {evoMonths.length > 0 ? (
              <ReactECharts option={buildEvoOption(evoMonths, evoIngresosReal, evoIngresosPpto, "#2A5BFF", evoCurrentIdx)} style={{ height: 180 }} opts={{ renderer: "svg" }} notMerge />
            ) : (
              <p style={{ textAlign: "center", color: "var(--ce-fg-muted)", padding: 40 }}>Sin datos</p>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHead icon={I.Coin} title="Evolución mensual – Gastos" />
          <CardBody style={{ padding: "8px 12px" }}>
            {evoMonths.length > 0 ? (
              <ReactECharts option={buildEvoOption(evoMonths, evoGastosReal, evoGastosPpto, "#EF4444", evoCurrentIdx)} style={{ height: 180 }} opts={{ renderer: "svg" }} notMerge />
            ) : (
              <p style={{ textAlign: "center", color: "var(--ce-fg-muted)", padding: 40 }}>Sin datos</p>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHead icon={I.BarChart3} title="Evolución mensual – Resultado" />
          <CardBody style={{ padding: "8px 12px" }}>
            {evoMonths.length > 0 ? (
              <ReactECharts option={buildEvoOption(evoMonths, evoResultadoReal, evoResultadoPpto, "#10B981", evoCurrentIdx)} style={{ height: 180 }} opts={{ renderer: "svg" }} notMerge />
            ) : (
              <p style={{ textAlign: "center", color: "var(--ce-fg-muted)", padding: 40 }}>Sin datos</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* ══ Resumen por áreas ══ */}
      <div style={{ marginTop: 20 }}>
        <Card>
          <CardHead icon={I.Layers} title="Resumen por áreas – Mes cerrado" />
          <CardBody style={{ padding: "8px 12px" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Área</th>
                  <th className="right" style={{ fontSize: 10 }}>Real mes<br/>(€)</th>
                  <th className="right" style={{ fontSize: 10 }}>Presupuesto<br/>mes (€)</th>
                  <th className="right" style={{ fontSize: 10 }}>Var. vs<br/>Presup. (€)</th>
                  <th className="right" style={{ fontSize: 10 }}>Var. vs<br/>Presup. (%)</th>
                </tr>
              </thead>
              <tbody>
                <AreaRow area={makeGroupRow("PRODUCCION", "Producción", "#2A5BFF")} isGroup />
                {areasProd.map(a =>
                  <AreaRow key={a.area} area={makeSubRow(a)} indent />
                )}

                <AreaRow area={makeGroupRow("GROWTH", "Growth", "#0AB4D4")} isGroup />
                {areasGrowth.map(a =>
                  <AreaRow key={a.area} area={makeSubRow(a)} indent />
                )}

                <AreaRow area={makeGroupRow("CORPORATE_SUPPORT", "Corporate Support", "#8B5CF6")} isGroup />
                {areasCorp.map(a =>
                  <AreaRow key={a.area} area={makeSubRow(a)} indent />
                )}

                <tr style={{ fontWeight: 700, borderTop: "2px solid var(--ce-border-strong)" }}>
                  <td>Total</td>
                  <td className="right">{fmtEur(resumen.gastos.real)}</td>
                  <td className="right">{fmtEur(resumen.gastos.presupuesto)}</td>
                  <td className="right">{fmtDelta(resumen.gastos.eur)}</td>
                  <td className="right">
                    <span className={`kpi-pill ${tone(totalPct, "contextual")}`}>
                      {totalPct != null ? `${totalPct >= 0 ? "+" : ""}${totalPct.toFixed(1).replace(".",",")}%` : "—"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>

      {/* ══ FILA 2 — Gastos externos + Tesorería + Cumplimiento ══ */}
      <div className="grid gc-3" style={{ marginTop: 20, alignItems: "start" }}>

        {/* ── C) Distribución gastos externos ── */}
        <Card>
          <CardHead icon={I.PieChart} title="Distribución de gastos externos por función" sub="(Mes cerrado)" />
          <CardBody>
            {donutData.length > 0 ? (
              <div>
                <ReactECharts
                  option={buildDonutOption(donutData, { value: fmtEur(gastos_externos?.total || 0), label: "Total externo" })}
                  style={{ height: 200 }} opts={{ renderer: "svg" }} notMerge
                />
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "4px 16px", fontSize: 11 }}>
                  {donutData.map(d => (
                    <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                      <span>{d.label}</span>
                      <span style={{ fontWeight: 600 }}>{(gastos_externos?.total ? (d.value / gastos_externos.total * 100) : 0).toFixed(1).replace(".",",")}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ textAlign: "center", color: "var(--ce-fg-muted)", padding: 40, fontSize: 12 }}>Sin gastos externos clasificados</p>
            )}
          </CardBody>
        </Card>

        {/* ── D) Capital circulante y tesorería ── */}
        <Card>
          <CardHead icon={I.Bank} title="Capital circulante y tesorería" />
          <CardBody style={{ padding: "10px 14px" }}>
            {(() => {
              const dsoReal = dso?.dso_dias != null ? Math.round(dso.dso_dias) : null;
              const pendReal = facturasPendientes?.total_pendiente;
              const saldoReal = saldos?.total_global;
              const runwayReal = saldoReal != null && resumen?.gastos?.real ? parseFloat((saldoReal / resumen.gastos.real).toFixed(1)) : null;

              const metrics = [
                { label: "DSO (días)", icon: I.Info, real: dsoReal, ppto: null, fmt: v => v, delta: null, sentido: "menor_mejor" },
                { label: "Pendiente de cobro", real: pendReal, ppto: null, fmt: fmtEur, delta: null, sentido: "menor_mejor" },
                { label: "Runway (meses)", real: runwayReal, ppto: null, fmt: v => v, delta: null, sentido: "mayor_mejor" },
                { label: "Saldo bancos", real: saldoReal, ppto: null, fmt: fmtEur, delta: null, sentido: "mayor_mejor" },
              ];

              return (
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  {metrics.map(m => (
                    <div key={m.label} style={{ flex: "1 1 0", minWidth: 0, borderLeft: "3px solid var(--ce-border-subtle)", paddingLeft: 10 }}>
                      <div style={{ fontSize: 10, color: "var(--ce-fg-muted)", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                        {m.label}
                      </div>
                      <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                        <div>
                          <div style={{ fontSize: 9, color: "var(--ce-fg-faint)" }}>Real</div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{m.real != null ? m.fmt(m.real) : "—"}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, color: "var(--ce-fg-faint)" }}>Presupuesto</div>
                          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ce-fg-muted)" }}>{m.ppto != null ? m.fmt(m.ppto) : "—"}</div>
                        </div>
                      </div>
                      {m.real != null && m.ppto != null && (
                        <div style={{ fontSize: 11, marginTop: 3, color: "var(--ce-fg-muted)" }}>
                          {fmtDelta(m.real - m.ppto, m.label.includes("días") || m.label.includes("meses") ? "" : "€")}
                        </div>
                      )}
                    </div>
                  ))}
                  {saldoEvo.length > 1 && (
                    <div style={{ flex: "1.2 1 0", minWidth: 120 }}>
                      <div style={{ fontSize: 10, color: "var(--ce-fg-muted)", marginBottom: 4 }}>Saldo bancos (evolución)</div>
                      <ReactECharts style={{ height: 80 }} opts={{ renderer: "svg" }} notMerge option={{
                        grid: { left: 40, right: 8, top: 8, bottom: 18 },
                        xAxis: { type: "category", data: saldoEvo.map(e => MESES_LABEL[e.mes - 1]), axisLabel: { fontSize: 8, color: "#bbb" }, axisLine: { show: false }, axisTick: { show: false } },
                        yAxis: { type: "value", splitNumber: 2, axisLabel: { fontSize: 8, color: "#bbb", formatter: v => fmtEurK(v) }, splitLine: { lineStyle: { color: "#f5f5f5" } }, axisLine: { show: false }, axisTick: { show: false } },
                        series: [{ type: "line", data: saldoEvo.map(e => e.saldo), symbol: "circle", symbolSize: 5, lineStyle: { width: 2, color: "#10B981" }, itemStyle: { color: "#10B981" }, areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "#10B98120" }, { offset: 1, color: "#10B98102" }] } } }],
                        tooltip: { trigger: "axis", formatter: p => `${p[0].name}: ${fmtEur(p[0].value)}` },
                      }} />
                    </div>
                  )}
                </div>
              );
            })()}
          </CardBody>
        </Card>

        {/* ── E) Cumplimiento presupuesto ── */}
        <Card>
          <CardHead icon={I.Target} title="Cumplimiento presupuesto" sub="(Mes cerrado)" />
          <CardBody>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
              <GaugeCircle pct={cumplimiento.ventas} label="Ventas" sentido="mayor_mejor" />
              <GaugeCircle pct={cumplimiento.gastos} label="Gastos" sentido="menor_mejor" />
              <GaugeCircle pct={cumplimiento.resultado} label="Resultado" sentido="mayor_mejor" />
              <GaugeCircle pct={cumplimiento.margen?.real != null && cumplimiento.margen?.presupuesto
                ? Math.round(cumplimiento.margen.real / cumplimiento.margen.presupuesto * 100)
                : null}
                label="Margen operativo" sentido="mayor_mejor" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* ── Footer ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, padding: "12px 0", borderTop: "1px solid var(--ce-border-subtle)", fontSize: 11, color: "var(--ce-fg-faint)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <I.Info size={12} />
          Todas las comparaciones de esta vista mensual son frente al Presupuesto mensual.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <I.Clock size={12} />
          Datos actualizados al {meta?.mes ? `${new Date(year, meta.mes, 0).getDate()}/${String(meta.mes).padStart(2,"0")}/${year}` : "—"}
        </div>
      </div>
    </div>
  );
};

export default DashboardFinanzasV2;
